'use client'
import { Skeleton } from '@/components/ui/skeleton'
import { TagThumbnail } from '@/components/ui/tag-thumbnail'
import { useCampaignPrice } from '@/hooks/common/use-campaign-price'
import { useIsMobileClient } from '@/hooks/common/use-device-type'
import { useCoolClubPrice } from '@/hooks/use-coolclub-price'
import { cn, formatNumber, formatPrice } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import Link from 'next/link'
import { AmazonProductItemVariant } from './amazon-product-item-variant'
import { AmazonProductItemVariantMobile } from './amazon-product-item-variant-mobile'
import { useAmazonProductCardContext } from './context/amazon-product-card-context'

export const AmazonProductItemInfo = ({
  gaTrackingValue,
  gaTrackingLabel,
  isSoldProduct,
}: {
  gaTrackingValue?: string
  gaTrackingLabel?: string
  isSoldProduct?: boolean
}) => {
  const { product, trackingClickProduct, theme } = useAmazonProductCardContext()
  const isMobile = useIsMobileClient()
  const { productData, isCoolClubSale } = useCoolClubPrice(product)
  const t = useTranslations('product_detail')

  const {
    finalPrice: campaignPrice,
    originalPrice: campaignOriginalPrice,
    discountPercent: campaignDiscountPercent,
    shouldShowDiscount: campaignShouldShowDiscount,
    isLoading: isCampaignLoading,
  } = useCampaignPrice({
    regularPrice: product?.regular_price ?? 0,
    comparePrice: product?.compare_price ?? 0,
    productId: product.id,
    product: product,
  })

  const getDisplayPrice = () => {
    if (campaignShouldShowDiscount) return campaignPrice
    return productData?.regular_price ?? 0
  }

  const getDisplayComparePrice = () => {
    if (campaignShouldShowDiscount && campaignOriginalPrice !== campaignPrice) {
      return campaignOriginalPrice
    }
    if (
      productData?.compare_price &&
      productData.compare_price !== productData.regular_price
    ) {
      return productData.compare_price
    }
    return undefined
  }

  const getDisplayDiscountPercent = () => {
    if (campaignShouldShowDiscount && campaignDiscountPercent > 0) {
      return campaignDiscountPercent
    }
    return productData?.sale_percent
  }

  const displayPrice = getDisplayPrice()
  const displayComparePrice = getDisplayComparePrice()
  const displayDiscountPercent = getDisplayDiscountPercent()

  const handleClick = () => {
    trackingClickProduct()
  }

  return (
    <figcaption className='flex flex-col gap-1 px-3 pb-4 lg:gap-2'>
      {isMobile ? (
        <AmazonProductItemVariantMobile />
      ) : (
        <AmazonProductItemVariant />
      )}

      <ProductTitleLink
        href={productData?.href}
        title={productData?.title}
        onClick={handleClick}
        gaTrackingValue={gaTrackingValue}
        gaTrackingLabel={gaTrackingLabel}
        theme={theme}
      />

      <ProductPriceBlock
        isLoading={isCampaignLoading}
        isCoolClubSale={isCoolClubSale}
        displayPrice={displayPrice}
        displayDiscountPercent={displayDiscountPercent}
        displayComparePrice={displayComparePrice}
        theme={theme}
      />

      {productData?.coolclub_sale?.active && !isCoolClubSale && (
        <p
          className={cn(
            'line-clamp-1 text-xs text-neutral-500',
            theme === 'dark' && 'text-neutral-400'
          )}
        >
          {t('title.coolclub_price_label')}:{' '}
          {formatPrice(productData.coolclub_sale.coolclub_price)}
        </p>
      )}

      {productData?.note && (
        <p className='line-clamp-2 font-sans text-xs italic leading-3.5 text-primary-500 lg:text-sm lg:leading-4.5'>
          {productData.note}
        </p>
      )}

      <span className='sr-only'>
        {`${t('sr.product', {
          title: productData?.title ?? '',
          price: isCampaignLoading ? '...' : formatPrice(displayPrice),
        })}${
          !isCampaignLoading && displayDiscountPercent && displayComparePrice
            ? t('sr.discount_part', {
                discount: displayDiscountPercent,
                comparePrice: formatPrice(displayComparePrice),
              })
            : ''
        }${productData?.note ? t('sr.note_prefix', { note: productData.note }) : ''}`}
      </span>

      {isSoldProduct &&
      productData?.sold_product &&
      productData.sold_product > 0 ? (
        <p
          className={cn(
            'text-xs italic md:text-sm',
            theme === 'dark' ? 'text-white' : 'text-neutral-700'
          )}
        >
          <span
            className={cn(
              'font-semibold',
              theme === 'dark' ? 'text-white' : 'text-primary-500'
            )}
          >
            {formatNumber(productData.sold_product)}+
          </span>{' '}
          lượt bán trên Amazon
        </p>
      ) : null}
    </figcaption>
  )
}

const ProductTitleLink = ({
  href,
  title,
  onClick,
  gaTrackingValue,
  gaTrackingLabel,
  theme,
}: {
  href?: string
  title?: string
  onClick: () => void
  gaTrackingValue?: string
  gaTrackingLabel?: string
  theme?: 'light' | 'dark'
}) => {
  return (
    <Link
      href={href || '/'}
      className='w-fit'
      aria-label={`Xem chi tiết sản phẩm ${title}`}
      onClick={onClick}
      prefetch={false}
      {...(gaTrackingValue &&
        gaTrackingLabel && {
          'ga-tracking-value': gaTrackingValue,
          'ga-tracking-label': gaTrackingLabel,
        })}
    >
      <h3
        className={cn(
          'line-clamp-2 font-sans text-xs leading-3.5 text-neutral-800 lg:text-base lg:leading-5',
          theme === 'dark' ? 'text-white' : 'hover:text-primary'
        )}
      >
        {title}
      </h3>
    </Link>
  )
}

const ProductPriceBlock = ({
  isLoading,
  isCoolClubSale,
  displayPrice,
  displayDiscountPercent,
  displayComparePrice,
  theme,
}: {
  isLoading: boolean
  isCoolClubSale: boolean
  displayPrice: number
  displayDiscountPercent?: number
  displayComparePrice?: number
  theme?: 'light' | 'dark'
}) => (
  <div className='flex flex-wrap items-center gap-1'>
    {isCoolClubSale && (
      <Image
        src='/icons/icon-price-signin.png'
        alt='Biểu tượng giá ưu đãi khi đăng nhập'
        width={16}
        height={16}
        loading='lazy'
        decoding='async'
      />
    )}
    {isLoading ? (
      <>
        <Skeleton className='h-4 w-16 rounded bg-neutral-200' />
        <Skeleton className='h-4 w-10 rounded bg-neutral-200' />
      </>
    ) : (
      <>
        <p
          className={cn(
            'font-sans text-xs font-bold leading-3.5 text-neutral-900 lg:text-base lg:leading-5',
            isCoolClubSale && 'text-primary',
            theme === 'dark' && 'text-white'
          )}
        >
          {formatPrice(displayPrice)}
        </p>
        {displayDiscountPercent ? (
          <TagThumbnail
            title={`-${displayDiscountPercent}%`}
            variant='primary'
            size='small'
            displayDiscountPercent={displayDiscountPercent}
            className='px-1.5 text-xxs font-semibold leading-3 text-light lg:text-sm lg:leading-4.5'
          />
        ) : null}
        {displayComparePrice ? (
          <p
            className={cn(
              'text-xs leading-3.5 text-neutral-500 line-through lg:text-sm lg:leading-4.5',
              theme === 'dark' && 'text-neutral-500'
            )}
          >
            {formatPrice(displayComparePrice)}
          </p>
        ) : null}
      </>
    )}
  </div>
)
